import * as React from 'react';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { composeRefs } from '../internals/composeRefs';
import { Checkbox, type CheckboxProps } from '../primitives/Checkbox';
import { FormField } from '../patterns/FormField';

import { rhfInlineError } from './rhfInlineError';
import type { RhfFormFieldShellProps, RhfLayoutProps } from './rhfLayout';

export interface RhfCheckboxProps extends Omit<CheckboxProps, 'checked'>, RhfLayoutProps, RhfFormFieldShellProps {
  control: Control<FieldValues>;
  name: string;
  label: string;
  required?: boolean;
  semanticId?: string;
  reserveLabelSpace?: boolean;
}

export const RhfCheckbox = React.forwardRef<HTMLInputElement, RhfCheckboxProps>(
  function RhfCheckbox({ control, name, label, hint, required = false, semanticId, disabled, id, controlWidth, reserveLabelSpace, onChange, tooltip, ...rest }, ref) {
    const controlId = id ?? name;

    return (
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState, formState }) => {
          const errorText = rhfInlineError(fieldState, { isSubmitted: formState.isSubmitted });
          return (
            <FormField
              hint={hint}
              semanticId={semanticId}
              disabled={disabled}
              error={errorText}
              controlWidth={controlWidth}
              reserveLabelSpace={reserveLabelSpace}
            >
              {/*
               * Pass `label` directly into Checkbox so it renders inside the
               * wrapping <label> element. This makes clicking the text toggle
               * the checkbox without relying on htmlFor/id pairing, which can
               * fail when the field name contains dots (repeatable form paths).
               */}
              <Checkbox
                ref={composeRefs(field.ref, ref)}
                id={controlId}
                name={field.name}
                label={label}
                checked={Boolean(field.value)}
                error={Boolean(errorText)}
                onChange={(e) => {
                  onChange?.(e);
                  field.onChange(e.currentTarget.checked);
                }}
                onBlur={field.onBlur}
                disabled={disabled}
                semanticId={semanticId}
                {...rest}
              />
            </FormField>
          );
        }}
      />
    );
  },
);

RhfCheckbox.displayName = 'RhfCheckbox';
